import { Component, OnDestroy, OnInit } from '@angular/core';
import { GlobalFunctions } from '@app/Global/GlobalFunctions';
import { animate, style, transition, trigger } from '@angular/animations';
import { ApiService } from '@app/Services/APIService';
import { NotifyService } from '@app/Services/NotifyService';
import { Router } from '@angular/router';
import { ClientDataStore } from '@app/Global/ClientDataStore';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, delay, distinctUntilChanged, map, mergeMap, of, Subject, Subscription } from 'rxjs';
import { ApplicationDetail } from '@app/Components/ApplicationSubmissionSummary/ApplicationDetail/ApplicationDetail';

@Component({
    selector: 'ApplicationSubmissionSummary',
    templateUrl: './ApplicationSubmissionSummary.html',
    styleUrls: ['./ApplicationSubmissionSummary.scss'],
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: '0' }),
                animate('0.1s ease-out', style({ opacity: '1' })),
            ]),
        ]),
    ]
})

export class ApplicationSubmissionSummary implements OnInit, OnDestroy {

    //Constructor
    constructor(private globalFunctions: GlobalFunctions,
        private apiService: ApiService,
        private notifyService: NotifyService,
        private router: Router,
        private clientDataStore: ClientDataStore,
        public dialog: MatDialog) {
    }

    //To store the application submissions
    public ApplicationSubmissionSummaryList;

    //Used by paginator to control what page is currently clicked
    public CurrentPage: number;

    //Used by paginator for config, size of the pages, etc.
    public Paging = {
        maxSize: 10,
        previousLabel: "",
        nextLabel: ""
    }

    //Text display limit
    public TextDisplayLimit = 30;
    public TextDisplayLimitReference = 25;

    //Used for the filter search.
    public FilterSearchValue = "";
    public FilterSearchValueKeyUp = new Subject<any>();

    //Subscription for the filter
    public Subscription: Subscription;

    //For tracking if a server update request is in progress
    public IsLoadingInProgress = false;

    //Angular startup method
    ngOnInit() {
        //We still need a short delay even this method is added on ngAfterViewInit
        this.globalFunctions.delay(10).then(() => {
            this.ApplicationSubmissionSummary_Get();
        });

        //This creates a subscription to apply the search filter as per the search box. 
        this.Subscription = this.FilterSearchValueKeyUp.pipe(
            map((event) => {
                return (<HTMLInputElement>event.target).value;
            }),
            debounceTime(10),
            distinctUntilChanged(),
            mergeMap(search => of(search).pipe(delay(10)))
        ).subscribe(value => {

            //Call it directly with a blank string if the value is empty
            if (this.globalFunctions.isEmpty(value)) {
                this.ApplicationSubmissionSummaryFilter_Search("");
            }
            else {
                //Filter using the value entered
                this.ApplicationSubmissionSummaryFilter_Search(value);
            }
        });
    }

    //Destroy the subscription
    ngOnDestroy() {
        if (!this.globalFunctions.isEmpty(this.Subscription)) {
            this.Subscription.unsubscribe();
        }
    }

    //To change the page for the paginator
    public Page_OnChange(page: number, doScroll = true) {
        this.CurrentPage = page;

        //Dont scroll to top, bad UI experience. scroll to bottom instead
        if (doScroll) {
            this.globalFunctions.delay(10).then(() => {
                window.scrollTo(0, document.body.scrollHeight);
            });
        }
    }

    //Refresh application submissions
    public ApplicationSubmissionSummary_Refresh() {
        //Clear search item if there is any
        this.FilterSearchValue = "";

        //Refresh the application submissions screen
        this.ApplicationSubmissionSummaryFilter_Search(this.FilterSearchValue);

        //We should trigger the paginator to cycle back to the first page, since it may be on an illegal page after refreshing.
        this.Page_OnChange(0, false);
    }

    //Send the request to get the list of application submissions
    public ApplicationSubmissionSummary_Get() {
        //Track state of this request, so that we can show the spinner, if necessary.
        this.IsLoadingInProgress = true;
        this.clientDataStore.SetShowFullscreenLoading(true);

        // In a real implementation, we would make an API call here
        // For this example, we'll create dummy data
        this.globalFunctions.delay(1000).then(() => {
            this.ApplicationSubmissionSummaryList = this.createDummyApplicationData();
            this.IsLoadingInProgress = false;
            this.clientDataStore.SetShowFullscreenLoading(false);
        });
    }

    //Create dummy application data for demonstration
    private createDummyApplicationData() {
        const applications = [];
        const statuses = ['Submitted', 'In Progress'];
        const products = ['P & I Home Loan', 'IO Home Loan'];
        const names = ['Suren Maharjan', 'Saroj Pokhrel', 'Payal Kantaria', 'Robert Dyball'];

        for (let i = 1; i <= 8; i++) {
            const date = new Date();
            date.setDate(date.getDate() - Math.floor(Math.random() * 30));

            const application = {
                ApplicationID: 1000 + i,
                ReferenceNumber: 'FIN' + (10000 + Math.floor(Math.random() * 90000)),
                Lender: "Finexia",
                Product: products[Math.floor(Math.random() * products.length)],
                ApplicantName: names[Math.floor(Math.random() * names.length)],
                RequestedAmount: Math.floor(Math.random() * 500000) + 50000,
                SubmissionDate: date.toISOString(),
                Status: statuses[Math.floor(Math.random() * statuses.length)],
                // Formatted versions for display
                RequestedAmount_FMT: '',
                SubmissionDate_FMT: '',
                // For tooltips
                DisableApplicantToolTip: true,
                DisableProductToolTip: true,
                DisableLenderToolTip: true,
                DisableReferenceToolTip: true
            };

            // Format the values
            application.RequestedAmount_FMT = this.globalFunctions.customDataTypeParser(application.RequestedAmount, "currency.2", "aus");
            application.SubmissionDate_FMT = this.globalFunctions.getCustomDateFormat(application.SubmissionDate, "shortdate", "custom", "YYYY-MM-DD");

            // Set tooltip flags
            if (application.ApplicantName.length >= this.TextDisplayLimit) {
                application.DisableApplicantToolTip = false;
            }

            if (application.Product.length >= this.TextDisplayLimit) {
                application.DisableProductToolTip = false;
            }

            if (application.Lender.length >= this.TextDisplayLimit) {
                application.DisableLenderToolTip = false;
            }

            if (application.ReferenceNumber.length >= this.TextDisplayLimitReference) {
                application.DisableReferenceToolTip = false;
            }

            applications.push(application);
        }

        return applications;
    }

    //Filter the applications list based on search term
    public ApplicationSubmissionSummaryFilter_Search(searchTerm: string) {
        if (this.globalFunctions.isEmpty(this.ApplicationSubmissionSummaryList)) {
            return;
        }

        // Filter applications based on search term
        if (this.globalFunctions.isEmpty(searchTerm)) {
            // If search term is empty, refresh the full list
            this.ApplicationSubmissionSummary_Get();
        } else {
            // Convert search term to lowercase for case-insensitive comparison
            const lowerSearchTerm = searchTerm.toLowerCase();

            // Create a filtered list based on the search term
            const filteredList = this.ApplicationSubmissionSummaryList.filter(app =>
                app.ApplicationID.toString().includes(lowerSearchTerm) === true ||
                app.ReferenceNumber.toLowerCase().includes(lowerSearchTerm) === true ||
                app.Lender.toLowerCase().includes(lowerSearchTerm) === true ||
                app.Product.toLowerCase().includes(lowerSearchTerm) === true ||
                app.ApplicantName.toLowerCase().includes(lowerSearchTerm) === true ||
                app.Status.toLowerCase().includes(lowerSearchTerm) === true
            );

            // Update the displayed list with filtered results
            this.ApplicationSubmissionSummaryList = filteredList;
        }

        // Reset to first page when filtering
        this.Page_OnChange(0, false);
    }

    //Clear the filter
    public ApplicationSubmissionSummaryFilter_Clear() {

        //Clear search item if there is any
        this.FilterSearchValue = "";

        //Refresh the application submissions screen
        this.ApplicationSubmissionSummary_Get();

        //We should trigger the paginator to cycle back to the first page, since it may be on an illegal page after refreshing.
        this.Page_OnChange(0, false);
    }

    //View application details (in a real implementation, this would navigate to a details page)
    public ApplicationDetails_View(applicationId: number) {
        this.notifyService.Success_Show(`Viewing application details for Application ID: ${applicationId}`, 'Application Details');
        // In a real implementation, you would navigate to the application details page
        // this.router.navigate([...]);
    }

    //Launch a new application submission modal dialog
    public NewApplication_Launch() {
        this.globalFunctions.FeatureModal_Launch(ApplicationDetail, this.globalFunctions.GetFeatureModalConfig("95%"), this.dialog, "New Application", 0, false, true);
    }
}
